import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { fetchVoicePreview, sayVoicePhrase } from '@/reducers/voiceAgents/voiceAgents.actions'

// Single-player audio controller for the voice picker: one clip plays at a
// time, canned previews are cached per voice for the session (they're static
// and free), and every object URL is revoked on unmount. `playingId` /
// `previewLoadingId` are voiceIds — the say-this clip plays under the chosen
// voice's id so its row shows the same playing affordance.
export const useVoiceAudio = ({ agentId, orgId }) => {
  const dispatch = useDispatch()
  const audioRef = useRef(null)
  const previewUrlByVoiceIdRef = useRef({})
  const sayUrlRef = useRef(null)
  const [playingId, setPlayingId] = useState(null)
  const [previewLoadingId, setPreviewLoadingId] = useState(null)

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setPlayingId(null)
  }

  const playUrl = (url, voiceId) => {
    stop()
    const audio = new Audio(url)
    audioRef.current = audio
    // Clear only if this clip is still the active one — a newer play() may
    // have replaced it by the time `ended` fires.
    const clear = () => setPlayingId((current) => (current === voiceId ? null : current))
    audio.onended = clear
    audio.onerror = clear
    setPlayingId(voiceId)
    // play() can reject (autoplay policy, undecodable blob) without firing
    // onerror — clear so the row doesn't show a stuck pause icon.
    audio.play().catch(clear)
  }

  const togglePreview = async (voice) => {
    if (playingId === voice.voiceId) {
      stop()
      return
    }
    let url = previewUrlByVoiceIdRef.current[voice.voiceId]
    if (!url) {
      setPreviewLoadingId(voice.voiceId)
      url = await dispatch(fetchVoicePreview(voice.previewUrl, orgId))
      setPreviewLoadingId(null)
      if (!url) {
        return
      }
      previewUrlByVoiceIdRef.current[voice.voiceId] = url
    }
    playUrl(url, voice.voiceId)
  }

  const sayPhrase = async (voiceId, transcript) => {
    const url = await dispatch(sayVoicePhrase({ agentId, voiceId, transcript, orgId }))
    if (!url) {
      return
    }
    // Each say generates a fresh clip — drop the previous one.
    if (sayUrlRef.current) {
      URL.revokeObjectURL(sayUrlRef.current)
    }
    sayUrlRef.current = url
    playUrl(url, voiceId)
  }

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause()
      }
      Object.values(previewUrlByVoiceIdRef.current).forEach((url) => URL.revokeObjectURL(url))
      if (sayUrlRef.current) {
        URL.revokeObjectURL(sayUrlRef.current)
      }
    },
    []
  )

  return { playingId, previewLoadingId, togglePreview, sayPhrase }
}
